
import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { JobStatus, JobResultSummary, JobsResponse } from '../types';
import { snowflakeApi } from '../utils/api';

// Global request throttling mechanism
const requestThrottleMap: Record<string, number> = {};
const REQUEST_THROTTLE_INTERVAL = 5000; // 5 seconds minimum between identical requests

function shouldThrottleRequest(key: string): boolean {
  const now = Date.now();
  const lastTime = requestThrottleMap[key] || 0;
  const timeDiff = now - lastTime;
  
  if (timeDiff < REQUEST_THROTTLE_INTERVAL) {
    console.warn(`Throttling request for ${key} (last request ${timeDiff}ms ago)`);
    return true;
  }
  
  requestThrottleMap[key] = now;
  return false;
}

// Interface definitions
export interface JobListParams {
  page?: number;
  page_size?: number;
  client_id?: string;
}

export interface JobCreationRequest {
  client_id: string;
  batch_set: string;
  input_blob: string;
  model?: string;
  batch_size?: number;
  priority?: number;
}

export interface CancelJobResponse {
  message: string;
}

export interface PromptConfig {
  system_prompt: string;
  output_format: string;
  temperature: number;
  max_tokens: number;
}

export interface ResultsConfig {
  output_format: string;
  output_regex: string;
  result_columns: string[];
}

export interface ClientConfig {
  config_id: string;
  name: string;
  description: string;
  topic_mapping: Record<number, string>;
  intent_mapping: Record<number, string>;
  funnel_mapping: Record<number, string>;
  subtopic_mappings: Record<string | number, { subtopics: Record<string, string | { name: string; source_module?: string; type?: string }> }>;
  topic_validations: Record<string | number, Record<string, any>>;
  prompt: PromptConfig;
  results: ResultsConfig;
  version: number;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

// Interface for job details
export interface JobDetails {
  job_id: string;
  client_id: string;
  batch_set: string;
  status: JobStatus;
  created_at: string;
  updated_at: string;
  total_items: number;
  processed_items: number;
  error?: string;
}

// Database-first interfaces
export interface DbBatchProcessRequest {
  workflowId: number;
  inputFile: string;
  configId?: number;
  batchSize?: number;
  priority?: number;
}

export interface DbBatchProcessResponse {
  id: string; // Unique process ID (UUID string)
  moduleId: number;
  workflowId: number;
  status: string;
  message?: string;
  error?: string;
  totalItems?: number;
  processedItems?: number;
  created_at?: string; // Add this - should match backend (ISO string)
  updated_at?: string; // Add this - should match backend (ISO string)
  started_at?: string; // Add this - from backend model (ISO string)
  completed_at?: string; // Add this - from backend model (ISO string)
  record_count?: number; // from backend model (legacy)
  error_count?: number; // from backend model (legacy)
  // New BatchJob statistics fields
  total_records?: number; // Real-time total records from BatchJob
  processed_records?: number; // Real-time processed records from BatchJob
  error_records?: number; // Real-time error records from BatchJob
  progress_percentage?: number; // Real-time progress percentage (0-100)
  batch_job_updated_at?: string; // When BatchJob stats were last updated (ISO string)
  success?: boolean; // from backend model
}

export interface DbConfigRequest {
  clientId: number;
  name: string;
  description: string;
  topicMapping: Record<number, string>;
  intentMapping: Record<number, string>;
  funnelMapping: Record<number, string>;
  subtopicMappings: Record<string | number, Record<number, string>>;
  topicValidations: Record<string | number, Record<string, any>>;
  prompt: PromptConfig;
  results: ResultsConfig;
  isActive?: boolean;
}

export interface DbConfig {
  configId: number;
  clientId: number;
  name: string;
  description: string;
  topicMapping: Record<number, string>;
  intentMapping: Record<number, string>;
  funnelMapping: Record<number, string>;
  subtopicMappings: Record<string | number, Record<number, string>>;
  topicValidations: Record<string | number, Record<string, any>>;
  prompt: PromptConfig;
  results: ResultsConfig;
  version: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DbJobResultSummary {
  moduleId: number;
  workflowId: number;
  totalItems: number;
  processedItems: number;
  successfulItems: number;
  failedItems: number;
  categories: string[];
  sampleResults: any[];
  resultsUrl?: string;
}

// Interface for module config response from /api/v1/db/module/{module_id}/config
export interface DbModuleConfigResponse {
  id: number;
  module_id: number;
  created_at: string;
  updated_at: string;
  config_data: {
    config_id: string;
    name: string;
    description: string;
    topic_mapping: Record<number, string | { name: string; source_module?: string; type?: string }>;
    intent_mapping: Record<number, string>;
    funnel_mapping: Record<number, string>;
    subtopic_mappings: Record<string | number, { topic_id?: number; subtopics: Record<number | string, string | { name: string; source_module?: string; type?: string }> } | Record<number, string>>;
    topic_validations: Record<string | number, Record<string, any>>;
    prompt: PromptConfig;
    results: ResultsConfig;
    version: number;
    is_active: boolean;
  };
  active: boolean;
  name: string;
  description: string;
}

// Service implementation
export class Module4Service {
  private static instance: Module4Service;

  private constructor() {}

  static getInstance(): Module4Service {
    if (!Module4Service.instance) {
      Module4Service.instance = new Module4Service();
    }
    return Module4Service.instance;
  }

  // Helper method to get Application Insights
  private getAppInsights() {
    return (window as any).__appInsights || null;
  }

  // Helper for throttling API requests
  private throttleRequest(key: string): Promise<void> {
    if (shouldThrottleRequest(key)) {
      console.warn(`Throttling API request: ${key}`);
      return new Promise(resolve => setTimeout(resolve, REQUEST_THROTTLE_INTERVAL));
    }
    return Promise.resolve();
  }

  async getHealth() {
    try {
      // Use Snowflake BFF proxy endpoint for Module 4 health check
      const response = await snowflakeApi.get('/api/v1/module4/health');
      return response.data;
    } catch (error) {
      console.error('Health check failed:', error);
      return { 
        status: 'failed', 
        module: 'module4',
        name: 'Batch Processing',
        timestamp: new Date().toISOString()
      };
    }
  }
  
  // Create a classification job
  public async createJob(request: JobCreationRequest): Promise<JobDetails> {
    console.log('Creating job:', request);
    
    // Track API call start in Application Insights
    const appInsights = this.getAppInsights();
    const startTime = Date.now();
    const correlationId = `module4-job-${Date.now()}`;
    
    if (appInsights) {
      appInsights.trackEvent({
        name: 'Module4API_CreateJobStarted',
        properties: {
          correlationId,
          clientId: request.client_id,
          batchSet: request.batch_set,
          operation: 'createJob',
          timestamp: new Date().toISOString()
        }
      });
    }
    
    try {
      const response = await snowflakeApi.post('/api/v1/jobs', request);
      
      // Track successful API call in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackEvent({
          name: 'Module4API_CreateJobCompleted',
          properties: {
            correlationId,
            clientId: request.client_id,
            batchSet: request.batch_set,
            jobId: response.data.job_id,
            operation: 'createJob',
            durationMs: duration,
            timestamp: new Date().toISOString()
          }
        });
      }
      
      return response.data;
    } catch (error) {
      console.error('Error creating job:', error);
      
      // Track API error in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackException({
          exception: error instanceof Error ? error : new Error('Unknown error in create job'),
          properties: {
            correlationId,
            clientId: request.client_id,
            batchSet: request.batch_set,
            operation: 'createJob',
            durationMs: duration,
            errorType: error instanceof Error ? error.name : typeof error,
            errorMessage: error instanceof Error ? error.message : String(error),
            timestamp: new Date().toISOString()
          }
        });
      }
      
      // Fallback to mock response
      return {
        job_id: 'mock-job-id',
        client_id: request.client_id,
        batch_set: request.batch_set,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        status: 'created',
        total_items: 0,
        processed_items: 0
      };
    }
  }

  // Get job status by ID
  public async getJobStatus(jobId: string): Promise<JobDetails> {
    console.log('Getting job status:', jobId);
    try {
      const response = await snowflakeApi.get(`/api/v1/jobs/${jobId}/status`);
      return response.data;
    } catch (error) {
      console.error('Error getting job status:', error);
      // Fallback to mock response
      return { 
        job_id: jobId, 
        client_id: 'mock-client',
        batch_set: 'mock-batch',
        status: 'processing',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        total_items: 100,
        processed_items: 50
      };
    }
  }

  // Get job details (alias for getJobStatus for backward compatibility)
  public async getJobDetails(jobId: string): Promise<JobDetails> {
    return this.getJobStatus(jobId);
  }

  // Get job results
  public async getJobResults(jobId: string): Promise<JobResultSummary> {
    console.log('Getting job results:', jobId);
    try {
      const response = await snowflakeApi.get(`/api/v1/jobs/${jobId}/results`);
      return response.data;
    } catch (error) {
      console.error('Error getting job results:', error);
      // Fallback to mock response
      return {
        job_id: jobId,
        client_id: 'mock-client',
        batch_set: 'mock-batch',
        total_items: 100,
        processed_items: 100,
        successful_items: 90,
        failed_items: 10,
        categories: [],
        sample_results: []
      };
    }
  }

  // List all jobs with optional filtering
  public async listJobs(params: JobListParams = {}): Promise<JobsResponse> {
    console.log('Listing jobs with params:', params);
    try {
      const response = await snowflakeApi.get('/api/v1/jobs', { params });
      return response.data;
    } catch (error) {
      console.error('Error listing jobs:', error);
      // Fallback to mock response
      return {
        jobs: [],
        total: 0
      };
    }
  }

  // Cancel a job
  public async cancelJob(jobId: string): Promise<CancelJobResponse> {
    console.log('Cancelling job:', jobId);
    try {
      const response = await snowflakeApi.post(`/api/v1/jobs/${jobId}/cancel`);
      return response.data;
    } catch (error) {
      console.error('Error cancelling job:', error);
      return { message: 'Job cancelled successfully' };
    }
  }

  // Configuration management methods
  public async getConfig(configId: string): Promise<ClientConfig> {
    console.log('Getting config:', configId);
    try {
      const response = await snowflakeApi.get(`/api/v1/configs/${configId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting config:', error);
      return { config_id: configId } as ClientConfig;
    }
  }

  public async createConfig(config: ClientConfig): Promise<ClientConfig> {
    console.log('Creating config:', config);
    try {
      const response = await snowflakeApi.post('/api/v1/configs', config);
      return response.data;
    } catch (error) {
      console.error('Error creating config:', error);
      return config;
    }
  }

  public async updateConfig(configId: string, config: Partial<ClientConfig>): Promise<ClientConfig> {
    console.log('Updating config:', configId, config);
    try {
      const response = await snowflakeApi.put(`/api/v1/configs/${configId}`, config);
      return response.data;
    } catch (error) {
      console.error('Error updating config:', error);
      return { ...config, config_id: configId } as ClientConfig;
    }
  }

  public async listConfigs(clientId?: string): Promise<ClientConfig[]> {
    console.log('Listing configs for client:', clientId);
    try {
      const params = clientId ? { client_id: clientId } : undefined;
      const response = await snowflakeApi.get('/api/v1/configs', { params });
      return response.data;
    } catch (error) {
      console.error('Error listing configs:', error);
      return [];
    }
  }

  public async deleteConfig(configId: string): Promise<{ message: string }> {
    console.log('Deleting config:', configId);
    try {
      const response = await snowflakeApi.delete(`/api/v1/configs/${configId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting config:', error);
      return { message: 'Config deleted successfully' };
    }
  }

  // DATABASE-FIRST METHODS
  
  // Start batch processing using database workflow ID
  public async startBatchProcessDb(request: DbBatchProcessRequest): Promise<DbBatchProcessResponse> {
    console.log('Starting batch process with database workflow ID:', request.workflowId);
    
    // Track API call start in Application Insights
    const appInsights = this.getAppInsights();
    const startTime = Date.now();
    const correlationId = `db-batch-process-${request.workflowId}-${Date.now()}`;
    
    if (appInsights) {
      appInsights.trackEvent({
        name: 'DbAPI_BatchProcessStarted',
        properties: {
          correlationId,
          workflowId: request.workflowId,
          configId: request.configId,
          operation: 'startBatchProcessDb',
          timestamp: new Date().toISOString()
        }
      });
    }
    
    try {
      // Prepare query parameters
      const params: Record<string, any> = {
        input_file: request.inputFile
      };
      
      if (request.configId) params.config_id = request.configId;
      if (request.batchSize) params.batch_size = request.batchSize;
      if (request.priority) params.priority = request.priority;
      
      // Call the module4 API
      const response = await snowflakeApi.post(
        `/api/v1/db/workflow/${request.workflowId}/process`,
        null,
        { params }
      );
      
      console.log('DB-first batch process raw API response.data:', response.data);
      
      // Map snake_case from API to camelCase for DbBatchProcessResponse interface
      const responseData = response.data;
      const mappedData: DbBatchProcessResponse = {
        id: responseData.id,
        moduleId: responseData.module_id, // Map from module_id
        workflowId: responseData.workflow_id, // Map from workflow_id
        status: responseData.status,
        message: responseData.message,
        error: responseData.error,
        totalItems: responseData.total_items,
        processedItems: responseData.processed_items,
        created_at: responseData.created_at,
        updated_at: responseData.updated_at,
        started_at: responseData.started_at,
        completed_at: responseData.completed_at,
        record_count: responseData.record_count,
        error_count: responseData.error_count,
        success: responseData.success
      };

      // Track successful API call in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackEvent({
          name: 'DbAPI_BatchProcessCompleted',
          properties: {
            correlationId,
            workflowId: request.workflowId,
            moduleId: responseData.module_id,
            operation: 'startBatchProcessDb',
            responseStatus: responseData?.status || 'unknown',
            durationMs: duration,
            timestamp: new Date().toISOString()
          }
        });
      }
      
      return mappedData; // Return the mapped data
    } catch (error) {
      console.error('DB-first batch process error:', error);
      
      // Track API error in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackException({
          exception: error instanceof Error ? error : new Error('Unknown error in DB-first batch process'),
          properties: {
            correlationId,
            workflowId: request.workflowId,
            configId: request.configId,
            operation: 'startBatchProcessDb',
            durationMs: duration,
            errorType: error instanceof Error ? error.name : typeof error,
            errorMessage: error instanceof Error ? error.message : String(error),
            timestamp: new Date().toISOString()
          }
        });
      }
      
      throw error;
    }
  }

  // Get batch process status by workflow ID
  public async getBatchStatusByWorkflowDb(workflowId: number, clientId?: number): Promise<DbBatchProcessResponse> {
    // Create a unique key for this request
    const requestKey = `batch-status-workflow-${workflowId}-${clientId || ''}`;
    
    // Check if we should throttle this request
    await this.throttleRequest(requestKey);
    
    console.log(`Getting batch status for workflow ID ${workflowId}`);
    
    // Track API call in Application Insights
    const appInsights = this.getAppInsights();
    const startTime = Date.now();
    const correlationId = `db-batch-status-workflow-${workflowId}-${Date.now()}`;
    
    if (appInsights) {
      appInsights.trackEvent({
        name: 'DbAPI_BatchStatusByWorkflowStarted',
        properties: {
          correlationId,
          workflowId,
          clientId,
          operation: 'getBatchStatusByWorkflowDb',
          timestamp: new Date().toISOString()
        }
      });
    }
    
    try {
      // Prepare query parameters
      const params: Record<string, any> = {};
      if (clientId !== undefined) {
        params.client_id = clientId;
      }
      
      const response = await snowflakeApi.get(`/api/v1/db/workflow/${workflowId}/status`, { params });
      
      // Track status check result in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackEvent({
          name: 'DbAPI_BatchStatusByWorkflowCompleted',
          properties: {
            correlationId,
            workflowId,
            clientId,
            moduleId: response.data?.module_id,
            operation: 'getBatchStatusByWorkflowDb',
            responseStatus: response.data?.status || 'unknown',
            durationMs: duration,
            timestamp: new Date().toISOString()
          }
        });
      }
      
      return response.data;
    } catch (error) {
      console.error(`Error getting batch status for workflow ID ${workflowId}:`, error);
      
      // Track error in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackException({
          exception: error instanceof Error ? error : new Error('Unknown error in DB batch status by workflow'),
          properties: {
            correlationId,
            workflowId,
            clientId,
            operation: 'getBatchStatusByWorkflowDb',
            durationMs: duration,
            errorType: error instanceof Error ? error.name : typeof error,
            errorMessage: error instanceof Error ? error.message : String(error),
            timestamp: new Date().toISOString()
          }
        });
      }
      
      throw error;
    }
  }
  
  // Get batch process status by module ID (maintained for backward compatibility)
  public async getBatchStatusDb(moduleId: number): Promise<DbBatchProcessResponse> {
    // Create a unique key for this request
    const requestKey = `batch-status-module-${moduleId}`;
    
    // Check if we should throttle this request
    await this.throttleRequest(requestKey);
    
    console.log(`Getting batch status for module ID ${moduleId}`);
    
    // Track API call in Application Insights
    const appInsights = this.getAppInsights();
    const startTime = Date.now();
    const correlationId = `db-batch-status-${moduleId}-${Date.now()}`;
    
    if (appInsights) {
      appInsights.trackEvent({
        name: 'DbAPI_BatchStatusCheckStarted',
        properties: {
          correlationId,
          moduleId,
          operation: 'getBatchStatusDb',
          timestamp: new Date().toISOString()
        }
      });
    }
    
    try {
      const response = await snowflakeApi.get(`/api/v1/db/module4/${moduleId}/status`);
      
      // Track status check result in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackEvent({
          name: 'DbAPI_BatchStatusCheckCompleted',
          properties: {
            correlationId,
            moduleId,
            operation: 'getBatchStatusDb',
            responseStatus: response.data?.status || 'unknown',
            durationMs: duration,
            timestamp: new Date().toISOString()
          }
        });
      }
      
      return response.data;
    } catch (error) {
      console.error(`Error getting batch status for module ID ${moduleId}:`, error);
      
      // Track error in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackException({
          exception: error instanceof Error ? error : new Error('Unknown error in DB batch status check'),
          properties: {
            correlationId,
            moduleId,
            operation: 'getBatchStatusDb',
            durationMs: duration,
            errorType: error instanceof Error ? error.name : typeof error,
            errorMessage: error instanceof Error ? error.message : String(error),
            timestamp: new Date().toISOString()
          }
        });
      }
      
      throw error;
    }
  }

  // Get batch results by workflow ID
  public async getBatchResultsByWorkflowDb(workflowId: number, clientId?: number): Promise<DbJobResultSummary> {
    console.log(`Getting batch results for workflow ID ${workflowId}`);
    
    // Track API call in Application Insights
    const appInsights = this.getAppInsights();
    const startTime = Date.now();
    const correlationId = `db-batch-results-workflow-${workflowId}-${Date.now()}`;
    
    if (appInsights) {
      appInsights.trackEvent({
        name: 'DbAPI_BatchResultsByWorkflowStarted',
        properties: {
          correlationId,
          workflowId,
          clientId,
          operation: 'getBatchResultsByWorkflowDb',
          timestamp: new Date().toISOString()
        }
      });
    }
    
    try {
      // Prepare query parameters
      const params: Record<string, any> = {};
      if (clientId !== undefined) {
        params.client_id = clientId;
      }
      
      const response = await snowflakeApi.get(`/api/v1/db/workflow/${workflowId}/results`, { params });
      
      // Track API call success in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackEvent({
          name: 'DbAPI_BatchResultsByWorkflowCompleted',
          properties: {
            correlationId,
            workflowId,
            clientId,
            moduleId: response.data?.module_id,
            operation: 'getBatchResultsByWorkflowDb',
            durationMs: duration,
            timestamp: new Date().toISOString()
          }
        });
      }
      
      return response.data;
    } catch (error) {
      console.error(`Error getting batch results for workflow ID ${workflowId}:`, error);
      
      // Track API error in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackException({
          exception: error instanceof Error ? error : new Error('Unknown error in DB batch results by workflow'),
          properties: {
            correlationId,
            workflowId,
            clientId,
            operation: 'getBatchResultsByWorkflowDb',
            durationMs: duration,
            errorType: error instanceof Error ? error.name : typeof error,
            errorMessage: error instanceof Error ? error.message : String(error),
            timestamp: new Date().toISOString()
          }
        });
      }
      
      throw error;
    }
  }
  
  // Get batch results by module ID (maintained for backward compatibility)
  public async getBatchResultsDb(moduleId: number): Promise<DbJobResultSummary> {
    console.log(`Getting batch results for module ID ${moduleId}`);
    
    // Track API call in Application Insights
    const appInsights = this.getAppInsights();
    const startTime = Date.now();
    const correlationId = `db-batch-results-${moduleId}-${Date.now()}`;
    
    if (appInsights) {
      appInsights.trackEvent({
        name: 'DbAPI_BatchResultsStarted',
        properties: {
          correlationId,
          moduleId,
          operation: 'getBatchResultsDb',
          timestamp: new Date().toISOString()
        }
      });
    }
    
    try {
      const response = await snowflakeApi.get(`/api/v1/db/module/${moduleId}/results`);
      
      // Track API call success in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackEvent({
          name: 'DbAPI_BatchResultsCompleted',
          properties: {
            correlationId,
            moduleId,
            operation: 'getBatchResultsDb',
            durationMs: duration,
            timestamp: new Date().toISOString()
          }
        });
      }
      
      return response.data;
    } catch (error) {
      console.error(`Error getting batch results for module ID ${moduleId}:`, error);
      
      // Track API error in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackException({
          exception: error instanceof Error ? error : new Error('Unknown error in DB batch results'),
          properties: {
            correlationId,
            moduleId,
            operation: 'getBatchResultsDb',
            durationMs: duration,
            errorType: error instanceof Error ? error.name : typeof error,
            errorMessage: error instanceof Error ? error.message : String(error),
            timestamp: new Date().toISOString()
          }
        });
      }
      
      throw error;
    }
  }

  // Cancel batch process by module ID
  public async cancelBatchDb(moduleId: number): Promise<{ message: string }> {
    console.log(`Cancelling batch process for module ID ${moduleId}`);
    
    try {
      const response = await snowflakeApi.post(`/api/v1/db/module/${moduleId}/cancel`);
      return response.data;
    } catch (error) {
      console.error(`Error cancelling batch for module ID ${moduleId}:`, error);
      throw error;
    }
  }

  // Get configuration by database ID
  public async getConfigDb(configId: number): Promise<DbConfig> {
    console.log(`Getting config by database ID ${configId}`);
    
    try {
      const response = await snowflakeApi.get(`/api/v1/db/config/${configId}`);
      return response.data;
    } catch (error) {
      console.error(`Error getting config with database ID ${configId}:`, error);
      throw error;
    }
  }

  // List configurations for a client
  public async listConfigsDb(clientId: number): Promise<DbConfig[]> {
    console.log(`Listing configs for client with database ID ${clientId}`);
    
    try {
      const response = await snowflakeApi.get(`/api/v1/db/client/${clientId}/configs`);
      return response.data.configs;
    } catch (error) {
      console.error(`Error listing configs for client with database ID ${clientId}:`, error);
      throw error;
    }
  }

  // Create a new configuration
  public async createConfigDb(config: DbConfigRequest): Promise<DbConfig> {
    console.log('Creating config with database client ID:', config.clientId);
    
    try {
      const response = await snowflakeApi.post('/api/v1/db/config', {
        client_id: config.clientId,
        name: config.name,
        description: config.description,
        topic_mapping: config.topicMapping,
        intent_mapping: config.intentMapping,
        funnel_mapping: config.funnelMapping,
        subtopic_mappings: config.subtopicMappings,
        topic_validations: config.topicValidations,
        prompt: config.prompt,
        results: config.results,
        is_active: config.isActive
      });
      return response.data;
    } catch (error) {
      console.error('Error creating config with database client ID:', error);
      throw error;
    }
  }

  // Update an existing configuration
  public async updateConfigDb(configId: number, config: Partial<DbConfigRequest>): Promise<DbConfig> {
    console.log(`Updating config with database ID ${configId}`);
    
    try {
      // Convert camelCase to snake_case for API
      const apiConfig: Record<string, any> = {};
      
      if (config.name) apiConfig.name = config.name;
      if (config.description) apiConfig.description = config.description;
      if (config.topicMapping) apiConfig.topic_mapping = config.topicMapping;
      if (config.intentMapping) apiConfig.intent_mapping = config.intentMapping;
      if (config.funnelMapping) apiConfig.funnel_mapping = config.funnelMapping;
      if (config.subtopicMappings) apiConfig.subtopic_mappings = config.subtopicMappings;
      if (config.topicValidations) apiConfig.topic_validations = config.topicValidations;
      if (config.prompt) apiConfig.prompt = config.prompt;
      if (config.results) apiConfig.results = config.results;
      if (config.isActive !== undefined) apiConfig.is_active = config.isActive;
      
      const response = await snowflakeApi.put(`/api/v1/db/config/${configId}`, apiConfig);
      return response.data;
    } catch (error) {
      console.error(`Error updating config with database ID ${configId}:`, error);
      throw error;
    }
  }

  // Delete a configuration
  public async deleteConfigDb(configId: number): Promise<{ message: string }> {
    console.log(`Deleting config with database ID ${configId}`);
    
    try {
      const response = await snowflakeApi.delete(`/api/v1/db/config/${configId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting config with database ID ${configId}:`, error);
      throw error;
    }
  }

  // Get module configuration by module ID
  public async getModuleConfigDb(moduleId: number): Promise<DbModuleConfigResponse> {
    // Create a unique key for this request
    const requestKey = `module-config-${moduleId}`;
    
    // Check if we should throttle this request
    await this.throttleRequest(requestKey);
    
    console.log(`Getting module config for module ID ${moduleId}`);
    
    // Track API call in Application Insights
    const appInsights = this.getAppInsights();
    const startTime = Date.now();
    const correlationId = `db-module-config-${moduleId}-${Date.now()}`;
    
    if (appInsights) {
      appInsights.trackEvent({
        name: 'DbAPI_GetModuleConfigStarted',
        properties: {
          correlationId,
          moduleId,
          operation: 'getModuleConfigDb',
          timestamp: new Date().toISOString()
        }
      });
    }
    
    try {
      // First try the primary endpoint (correct API endpoint)
      let response;
      try {
        response = await snowflakeApi.get(`/api/v1/db/module/${moduleId}`);
        console.log(`Successfully retrieved module config from primary endpoint for module ${moduleId}`);
      } catch (primaryError: any) {
        console.log(`Primary endpoint /api/v1/db/module/${moduleId} failed with status ${primaryError?.response?.status}`);
        
        // If primary endpoint fails with 404, the module doesn't exist
        if (primaryError?.response?.status === 404) {
          const errorMessage = `Module with ID ${moduleId} not found`;
          console.error(errorMessage);
          
          // Track the 404 error
          if (appInsights) {
            const duration = Date.now() - startTime;
            appInsights.trackException({
              exception: new Error(errorMessage),
              properties: {
                correlationId,
                moduleId,
                operation: 'getModuleConfigDb',
                durationMs: duration,
                errorType: 'ModuleNotFound',
                errorMessage,
                httpStatus: 404,
                timestamp: new Date().toISOString()
              }
            });
          }
          
          throw new Error(errorMessage);
        }
        
        // For other errors, try the legacy endpoint as fallback
        console.warn(`Endpoint /api/v1/db/module/${moduleId} failed, trying legacy endpoint with /config suffix`);
        try {
          response = await snowflakeApi.get(`/api/v1/db/module/${moduleId}/config`);
          console.log(`Successfully retrieved module config from legacy endpoint for module ${moduleId}`);
        } catch (legacyError: any) {
          console.error(`Legacy endpoint also failed for module ${moduleId}:`, legacyError);
          
          // Both endpoints failed, provide a meaningful error message
          const errorMessage = legacyError?.response?.status === 404 
            ? `Module with ID ${moduleId} not found`
            : `Failed to retrieve configuration for module ${moduleId}`;
          
          throw new Error(errorMessage);
        }
      }
      
      // Track API call success in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackEvent({
          name: 'DbAPI_GetModuleConfigCompleted',
          properties: {
            correlationId,
            moduleId,
            operation: 'getModuleConfigDb',
            durationMs: duration,
            timestamp: new Date().toISOString()
          }
        });
      }
      
      return response.data;
    } catch (error) {
      console.error(`Error getting module config for module ID ${moduleId}:`, error);
      
      // Track API error in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackException({
          exception: error instanceof Error ? error : new Error('Unknown error in get module config'),
          properties: {
            correlationId,
            moduleId,
            operation: 'getModuleConfigDb',
            durationMs: duration,
            errorType: error instanceof Error ? error.name : typeof error,
            errorMessage: error instanceof Error ? error.message : String(error),
            timestamp: new Date().toISOString()
          }
        });
      }
      
      throw error;
    }
  }
  
  // Save configuration to a module
  public async saveModuleConfigDb(moduleId: number, config: any): Promise<DbModuleConfigResponse> {
    console.log(`Saving config to module ID ${moduleId}`);
    
    // Track API call in Application Insights
    const appInsights = this.getAppInsights();
    const startTime = Date.now();
    const correlationId = `db-save-module-config-${moduleId}-${Date.now()}`;
    
    if (appInsights) {
      appInsights.trackEvent({
        name: 'DbAPI_SaveModuleConfigStarted',
        properties: {
          correlationId,
          moduleId,
          operation: 'saveModuleConfigDb',
          timestamp: new Date().toISOString()
        }
      });
    }
    
    try {
      // Structure the request to match what the exportToModule4 function uses
      const requestPayload = {
        config_data: config,
        module_id: moduleId,
        active: config.is_active !== undefined ? config.is_active : true,
        name: config.name || '',
        description: config.description || ''
      };
      
      // Use the same endpoint as the GET method but with POST
      const response = await snowflakeApi.post(`/api/v1/db/module/${moduleId}/config`, requestPayload);
      
      // Track API call success in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackEvent({
          name: 'DbAPI_SaveModuleConfigCompleted',
          properties: {
            correlationId,
            moduleId,
            operation: 'saveModuleConfigDb',
            durationMs: duration,
            timestamp: new Date().toISOString()
          }
        });
      }
      
      return response.data;
    } catch (error) {
      console.error(`Error saving module config for module ID ${moduleId}:`, error);
      
      // Track API error in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackException({
          exception: error instanceof Error ? error : new Error('Unknown error in save module config'),
          properties: {
            correlationId,
            moduleId,
            operation: 'saveModuleConfigDb',
            durationMs: duration,
            errorType: error instanceof Error ? error.name : typeof error,
            errorMessage: error instanceof Error ? error.message : String(error),
            timestamp: new Date().toISOString()
          }
        });
      }
      
      throw error;
    }
  }
}

// React Query hooks for database-first operations
export const useStartBatchProcessDb = () => {
  return useMutation({
    mutationFn: (request: DbBatchProcessRequest) => 
      Module4Service.getInstance().startBatchProcessDb(request),
  });
};

// Preferred workflow-based hooks with improved polling
export const useGetBatchStatusByWorkflowDb = (
  workflowId: number, 
  clientId?: number,
  options?: Partial<UseQueryOptions<any, unknown, any, any[]>>
) => {
  return useQuery({
    queryKey: ['dbBatchStatus', 'workflow', workflowId, clientId],
    queryFn: options?.queryFn || (() => Module4Service.getInstance().getBatchStatusByWorkflowDb(workflowId, clientId)),
    enabled: options?.enabled !== undefined ? options.enabled : !!workflowId,
    refetchInterval: options?.refetchInterval !== undefined ? options.refetchInterval : 10000, // Poll every 10 seconds
    ...options // Spread the rest of the options
  });
};

export const useGetBatchResultsByWorkflowDb = (
  workflowId: number, 
  clientId?: number,
  options?: Partial<UseQueryOptions<any, unknown, any, any[]>>
) => {
  return useQuery({
    queryKey: ['dbBatchResults', 'workflow', workflowId, clientId],
    queryFn: options?.queryFn || (() => Module4Service.getInstance().getBatchResultsByWorkflowDb(workflowId, clientId)),
    enabled: options?.enabled !== undefined ? options.enabled : !!workflowId,
    ...options
  });
};

// Deprecated module-based hooks with improved polling (maintained for backward compatibility)
export const useGetBatchStatusDb = (
  moduleId: number,
  options?: Partial<UseQueryOptions<any, unknown, any, any[]>>
) => {
  return useQuery({
    queryKey: ['dbBatchStatus', moduleId],
    queryFn: options?.queryFn || (() => Module4Service.getInstance().getBatchStatusDb(moduleId)),
    enabled: options?.enabled !== undefined ? options.enabled : !!moduleId,
    refetchInterval: options?.refetchInterval !== undefined ? options.refetchInterval : 10000, // Poll every 10 seconds
    ...options
  });
};

export const useGetBatchResultsDb = (moduleId: number) => {
  return useQuery({
    queryKey: ['dbBatchResults', moduleId],
    queryFn: () => Module4Service.getInstance().getBatchResultsDb(moduleId),
    enabled: !!moduleId
  });
};

export const useCancelBatchDb = () => {
  return useMutation({
    mutationFn: (moduleId: number) => 
      Module4Service.getInstance().cancelBatchDb(moduleId),
  });
};

export const useCreateConfigDb = () => {
  return useMutation({
    mutationFn: (config: DbConfigRequest) => 
      Module4Service.getInstance().createConfigDb(config),
  });
};

export const useUpdateConfigDb = () => {
  return useMutation({
    mutationFn: ({ configId, config }: { configId: number; config: Partial<DbConfigRequest> }) => 
      Module4Service.getInstance().updateConfigDb(configId, config),
  });
};

export const useListConfigsDb = (clientId: number) => {
  return useQuery({
    queryKey: ['dbConfigs', clientId],
    queryFn: () => Module4Service.getInstance().listConfigsDb(clientId),
    enabled: !!clientId
  });
};

export const useGetModuleConfigDb = (
  moduleId: number,
  options?: Partial<UseQueryOptions<any, unknown, any, any[]>>
) => {
  return useQuery({
    queryKey: ['dbModuleConfig', moduleId],
    queryFn: options?.queryFn || (() => Module4Service.getInstance().getModuleConfigDb(moduleId)),
    enabled: options?.enabled !== undefined ? options.enabled : !!moduleId,
    retry: (failureCount, error) => {
      // Don't retry if the error is a 404 (module not found)
      if (error instanceof Error && error.message.includes('not found')) {
        return false;
      }
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    staleTime: 60000, // Data is considered fresh for 1 minute
    cacheTime: 300000, // Keep in cache for 5 minutes
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    ...options
  });
};

export const useSaveModuleConfigDb = () => {
  return useMutation({
    mutationFn: ({ moduleId, config }: { moduleId: number; config: any }) => 
      Module4Service.getInstance().saveModuleConfigDb(moduleId, config),
  });
};

const module4Service = Module4Service.getInstance();
export default module4Service;
